import CameraAltIcon from '@mui/icons-material/CameraAlt'
import ModeEditIcon from '@mui/icons-material/ModeEdit'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import {
  Avatar,
  Box,
  Button,
  Container,
  CssBaseline,
  Link,
  Skeleton,
  Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AppLessonSkeleton,
  AppModal,
  ErrorPage,
  Header,
} from '../../../components'
import { THEME } from '../../../global'
import { useAuth } from '../../../hooks'
import expirationDate from '../../../resources/expirationDate'
import user from '../../../services/user'

type InfoType = 'password' | 'name' | 'email'

type InfoAreaProps = {
  type: 'password' | 'name' | 'email'
  handleOpenModal: (data: string, type: InfoType) => void
  data: string
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
})

export default function Profile() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const { logout } = useAuth()
  const [modalData, setModalData] = useState('')
  const [expireDate, setExpireDate] = useState<Date>()
  const [modalType, setModalType] = useState<InfoType>()

  const handleOpenModal = (data: string, type: InfoType) => {
    setModalType(type)
    setModalData(data)
    setOpen(true)
  }

  const {
    data: profileInfo,
    isLoading,
    isError,
  } = useQuery({
    queryFn: () => user.getProfileInfo(),
    queryKey: [`user-profile${open}`],
  })

  React.useEffect(() => {
    if (profileInfo && profileInfo.lastPaymentData) {
      const expired = expirationDate({
        date: new Date(profileInfo?.lastPaymentData),
        days: 30,
      })

      setExpireDate(expired)
    }
  }, [profileInfo])

  if (isError)
    return (
      <>
        <Header />
        <ErrorPage
          text={`Encontramos um erro ao buscar informações do seu perfil! Por favor, recarregue a página.`}
        />
      </>
    )

  return (
    <>
      <Container component="main">
        <CssBaseline />
        <Header>
          <div />
        </Header>

        <div className="flex flex-col items-center">
          <div className="relative">
            <Avatar
              sx={{
                border: 2,
                borderColor: THEME.colors.disabled,
                bgcolor: 'transparent',
                height: 124,
                width: 124,
                overflow: 'hidden',
              }}
            >
              <PersonOutlineIcon
                color="primary"
                style={{ width: 170, height: 170, marginBottom: -40 }}
              />
            </Avatar>
            <div className="absolute bottom-0 right-0 border-2 bg-secondary border-primary rounded-full">
              <div className="relative w-full h-full">
                <form action="">
                  <Button
                    component="label"
                    variant="contained"
                    sx={{
                      minWidth: 0,
                      minHeight: 0,
                      p: '0.25rem',
                      borderRadius: 50,
                      bgcolor: 'transparent',
                    }}
                  >
                    <CameraAltIcon
                      sx={{ width: 28, height: 28, color: 'white' }}
                    />
                    <VisuallyHiddenInput type="file" />
                  </Button>
                </form>
              </div>
            </div>
          </div>

          <Typography sx={{ m: 2 }}>Nível {profileInfo?.level}</Typography>
        </div>

        {!isLoading && (
          <div className="flex flex-col bg-tertiary p-3 rounded-[8px]">
            <div className="flex items-center justify-between">
              <Typography sx={{ fontSize: 24 }}>
                Conta {profileInfo?.signature}
              </Typography>

              <Button
                type="button"
                variant="contained"
                onClick={() => navigate('packages')}
                sx={{ width: '124px', boxShadow: 'none', height: '34px' }}
              >
                <p className="text-sm normal-case">Assinar</p>
              </Button>
            </div>

            <div className="flex justify-between mt-2">
              <Typography sx={{ fontSize: 10 }}>
                Ultima assinatura:
                {profileInfo?.lastPaymentData &&
                  format(new Date(profileInfo?.lastPaymentData), 'DD/MM/YYY')}
              </Typography>
              <Typography sx={{ fontSize: 10 }}>
                Ultima assinatura:
                {expireDate && format(expireDate, 'DD/MM/YYY')}
              </Typography>
            </div>
          </div>
        )}

        {!isLoading ? (
          <>
            <Typography sx={{ mt: 2 }}>Dados da conta</Typography>

            <UserInfoArea
              handleOpenModal={handleOpenModal}
              type="name"
              data={profileInfo ? profileInfo.name : '------'}
            />
            <UserInfoArea
              handleOpenModal={handleOpenModal}
              type="email"
              data={profileInfo ? profileInfo.email : '------'}
            />
            <UserInfoArea
              handleOpenModal={handleOpenModal}
              type="password"
              data={'******'}
            />
          </>
        ) : (
          <>
            <Skeleton
              variant="text"
              sx={{ fontSize: '1.5rem', width: '40%', mr: 1, mt: 2 }}
            />
            <AppLessonSkeleton isListMode />
          </>
        )}

        <Box sx={{ mt: 6 }}>
          <Link onClick={() => logout()}>Sair</Link>
        </Box>
      </Container>

      <AppModal
        data={modalData}
        setOpen={setOpen}
        open={open}
        type={modalType}
      />
    </>
  )
}

function UserInfoArea({ type = 'name', data, handleOpenModal }: InfoAreaProps) {
  const areaHelperInfo = {
    password: 'Palavra passe',
    name: 'Nome completo',
    email: 'E-mail',
  }

  return (
    <div
      onClick={() => handleOpenModal(data, type)}
      className="flex my-4 justify-between items-center border border-disabled rounded-[8px] px-3 py-2"
    >
      <div>
        <Typography>{type !== 'password' ? data : '*******'}</Typography>
        <Typography sx={{ fontSize: 12, color: THEME.colors.disabled }}>
          {areaHelperInfo[type]}
        </Typography>
      </div>

      <ModeEditIcon sx={{ fontSize: 28, color: THEME.colors.primary }} />
    </div>
  )
}
